/**
 * Typed Frappe client (WP-03.2). Same-origin in production (Traefik routes /api and /socket.io to ERPNext),
 * proxied by Vite in development. The OIDC bearer token is attached by the auth layer (WP-03.1); until then the
 * dev proxy relies on the Frappe session cookie.
 */
export type FrappeListArgs = {
  filters?: Record<string, unknown> | unknown[][];
  /** Any-of filters (Frappe `or_filters`), e.g. a search across several fields. */
  orFilters?: unknown[][];
  fields?: string[];
  orderBy?: string;
  limit?: number;
  start?: number;
};

let accessToken: string | null = null;
export const setAccessToken = (token: string | null) => { accessToken = token; };
/** Current Keycloak access token (the AI API forwards it to Frappe, so the assistant sees exactly what the person sees). */
export const getAccessToken = () => accessToken;

async function request<T>(url: string, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json", ...(init.headers as Record<string, string> ?? {}) };
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  const res = await fetch(url, { credentials: "include", ...init, headers });
  if (!res.ok) throw new Error(`Frappe ${res.status}: ${await res.text()}`);
  const body = await res.json();
  return (body.message ?? body.data ?? body) as T;
}

export const frappe = {
  /** GET /api/resource/<DocType> with JSON-encoded filters/fields (permissions apply server-side). */
  list<T = Record<string, unknown>>(doctype: string, args: FrappeListArgs = {}): Promise<T[]> {
    const q = new URLSearchParams();
    if (args.filters) q.set("filters", JSON.stringify(args.filters));
    if (args.orFilters && args.orFilters.length) q.set("or_filters", JSON.stringify(args.orFilters));
    if (args.fields) q.set("fields", JSON.stringify(args.fields));
    if (args.orderBy) q.set("order_by", args.orderBy);
    q.set("limit_page_length", String(args.limit ?? 50));
    if (args.start) q.set("limit_start", String(args.start));
    return request<T[]>(`/api/resource/${encodeURIComponent(doctype)}?${q}`);
  },
  get<T = Record<string, unknown>>(doctype: string, name: string): Promise<T> {
    return request<T>(`/api/resource/${encodeURIComponent(doctype)}/${encodeURIComponent(name)}`);
  },
  /** GET /api/method/<dotted.path> — whitelisted server methods (onesphere.api.*). An `undefined` value is
   * omitted (unlike `post`'s JSON.stringify body, URLSearchParams would otherwise stringify it to the literal
   * text "undefined" and the server would see a truthy filter instead of "not set"). */
  call<T = unknown>(method: string, params: Record<string, unknown> = {}): Promise<T> {
    const q = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, typeof v === "string" ? v : JSON.stringify(v)]));
    return request<T>(`/api/method/${method}?${q}`);
  },
  post<T = unknown>(method: string, body: Record<string, unknown>): Promise<T> {
    return request<T>(`/api/method/${method}`, { method: "POST", body: JSON.stringify(body), headers: { "Content-Type": "application/json" } });
  },
  /** DocType metadata for generated screens (WP-03.3). */
  meta(doctype: string) {
    return request<{ docs: unknown[] }>(`/api/method/frappe.desk.form.load.getdoctype?doctype=${encodeURIComponent(doctype)}`);
  },
};
